import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, FormGroup, InputGroup} from '@blueprintjs/core';

import {
  loadView,
  loadViews,
  loadWorkbooks,
} from '../../../store/actions/tableauActions';
import {IState} from '../../../store/reducers';
import {ApiStatus} from '../../../models';
import {IItem} from '../../select';
import SelectOptions from '../components/SelectOptionsFormElement';
import SelectAction from './SelectAction';
import SelectTableauItem from './SelectTableauItem';
import {
  createDefaultOption,
  getIntegrationWorkbooks,
  getViewCustomViews,
  getWorkbookViews,
  showOptionsFor,
  tableauItemsToOptions,
} from './utils';
import {
  ACTION_TYPES,
  IMAGE_RESOLUTION_OPTIONS,
  PDF_ORIENTATION_OPTIONS,
  PDF_PAGE_TYPE_OPTIONS,
  WORKBOOK_ACTIONS,
  WORKBOOKS_LIMIT,
} from './constants';
import PreviewImage from './PreviewImage';
import {redirectToExternal} from "../../subscription/utils";

import './TableauForm.scss';

interface TableauFormProps {
  uid: string;
  integrationId: string;
  action: string;
  workbookId: string;
  workbookName: string;
  viewId: string;
  viewName: string;
  customViewId: string;
  customViewName: string;
  filters: string;
  resolution: string;
  orientation: string;
  pageType: string;
  vizHeight: string;
  vizWidth: string;
  onActionChange: (value: string) => void;
  onWorkbookChange: (value: IItem) => void;
  onViewChange: (value: IItem) => void;
  onCustomViewChange: (value: string) => void;
  onFiltersChange: (value: string) => void;
  onResolutionChange: (value: string) => void;
  onOrientationChange: (value: string) => void;
  onPageTypeChange: (value: string) => void;
  onVizHeightChange: (value: string) => void;
  onVizWidthChange: (value: string) => void;
  disabled?: boolean;
  showActions?: boolean;
  showCustomViews?: boolean;
}

export const TableauForm = ({
  uid,
  integrationId,
  action,
  workbookId,
  workbookName,
  viewId,
  viewName,
  customViewId,
  customViewName,
  filters,
  resolution,
  orientation,
  pageType,
  vizHeight,
  vizWidth,
  onActionChange,
  onWorkbookChange,
  onViewChange,
  onCustomViewChange,
  onFiltersChange,
  onResolutionChange,
  onOrientationChange,
  onPageTypeChange,
  onVizHeightChange,
  onVizWidthChange,
  disabled = false,
  showActions = true,
  showCustomViews = true,
}: TableauFormProps) => {
  /*
   * Tableau items form (workbooks, views, custom views)
   */
  const [loadingWorkbooks, setLoadingWorkbooks] = useState<boolean>(false);
  const [loadingViews, setLoadingViews] = useState<boolean>(false);
  const [loadingView, setLoadingView] = useState<boolean>(false);
  const [loadWorkbookFilter, setLoadWorkbookFilter] = useState(false); // loading action on workbook filter
  const [workbookFiltered, setWorkbookFiltered] = useState(false);
  const [dashboardUrl, setDashboardUrl] = useState('');

  const allWorkbooks = useSelector((state: IState) => state.tableau.workbooks);
  const allViews = useSelector((state: IState) => state.tableau.views);
  const allCustomViews = useSelector((state: IState) => state.tableau.customViews);
  const loadingWorkbooksStatus = useSelector((state: IState) => state.tableau.loadingWorkbooksStatus);
  const loadingViewsStatus = useSelector((state: IState) => state.tableau.loadingViewsStatus);
  const loadingViewStatus = useSelector((state: IState) => state.tableau.loadingViewStatus);
  const dispatch = useDispatch();

  const workbooks = useMemo(
    () => getIntegrationWorkbooks(allWorkbooks, integrationId),
    [allWorkbooks, integrationId]);
  const views = useMemo(
    () => getWorkbookViews(allViews, integrationId, workbookId),
    [allViews, integrationId, workbookId]);
  const customViews = useMemo(
    () => getViewCustomViews(allCustomViews, integrationId, viewId),
    [allCustomViews, integrationId, viewId]);
  const selectedView = useMemo(
    () => views?.find((v: any) => v.id === viewId),
    [views, viewId]
  );
  const showView = useMemo(() =>
      !WORKBOOK_ACTIONS.includes(action),
    [action]);
  const showCustomView = useMemo(() =>
      action === ACTION_TYPES.VIEW_IMAGE && (customViews?.length > 0 || !!customViewId),
    [action, customViews?.length, customViewId]);
  const showFilters = useMemo(() =>
      /*
       * If action is not image with custom view, show filters
       */
      !(action == ACTION_TYPES.VIEW_IMAGE && customViewId),
    [action, customViewId]);
  const workbooksNo = useRef(0);

  // load views if there are no views and workbookId is present
  useEffect(() => {
    if (integrationId && workbookId && !views?.length) {
      dispatch(loadViews(uid, integrationId, workbookId));
    }
  }, [integrationId, workbookId]);

  useEffect(() => {
    if (!views?.length) return;

    const selected = views.find((v) => v.id === viewId);
    if (!selected) return;

    if (selected.tableau_url && selected.site_name && selected.content_url) {
      const content_url = selected.content_url.replace('/sheets/', '/');
      const base = `${selected.tableau_url}/#/site/${selected.site_name}/views/${content_url}`;
      const urlWithFilters = filters ? `${base}?${filters}` : base;
      setDashboardUrl(urlWithFilters);
    }
  }, [viewId, filters, views]);

  useEffect(() => {
    if (workbooksNo.current === 0) {
      /*
       * Set only once in order to know if we should use search via API or not
       */
      workbooksNo.current = workbooks?.length || 0;
    }
  }, [workbooks?.length]);

  useEffect(() => {
    if (loadWorkbookFilter && loadingWorkbooksStatus === ApiStatus.LOADED) {
      setLoadWorkbookFilter(false);
    }
  }, [loadWorkbookFilter, loadingWorkbooksStatus]);

  useEffect(() => {
    if (loadingWorkbooks) {
      setLoadingWorkbooks(loadingWorkbooksStatus === ApiStatus.LOADING);
    }
  }, [loadingWorkbooks, loadingWorkbooksStatus]);

  useEffect(() => {
    if (loadingViews) {
      setLoadingViews(loadingViewsStatus === ApiStatus.LOADING);
    }
  }, [loadingViews, loadingViewsStatus]);

  useEffect(() => {
    if (loadingView) {
      setLoadingView(loadingViewStatus === ApiStatus.LOADING);
    }
  }, [loadingView, loadingViewStatus]);

  const workbookOptions = useMemo(
    () => tableauItemsToOptions(workbooks),
    [integrationId, workbooks?.length]);
  const viewOptions = useMemo(() =>
      tableauItemsToOptions(views),
    [integrationId, workbookId, views?.length]);
  const customViewOptions = useMemo(() =>
      tableauItemsToOptions(customViews),
    [integrationId, viewId, customViews?.length]);

  const onSelectChange = useCallback(
    (callback: Function) => (value: any) => callback(value.value),
    []);

  const loadWorkbooksAction = useCallback((integrationId: string) => {
    setLoadingWorkbooks(true);
    dispatch(loadWorkbooks(uid as string, integrationId, undefined, true));
  }, [uid]);

  const loadViewsAction = useCallback((integrationId: string, workbookId: string) => {
    setLoadingViews(true);
    dispatch(loadViews(uid as string, integrationId, workbookId));
  }, [uid]);

  const loadViewAction = useCallback((integrationId: string, viewId: string) => {
    setLoadingView(true);
    dispatch(loadView(uid as string, integrationId, viewId));
  }, [uid]);

  const _onActionChange = (value: any) => {
    const actionValue = value.value;

    onActionChange(actionValue);

    if (!WORKBOOK_ACTIONS.includes(actionValue) && !views?.length && workbookId) {
      loadViewsAction(integrationId, workbookId);
    }
  };

  const _onWorkbookChange = (value: any) => {
    const workbookId = value.value;

    onWorkbookChange(value);

    if (!WORKBOOK_ACTIONS.includes(action) && workbookId) {
      const viewsNo = getWorkbookViews(allViews, integrationId, workbookId);

      if (viewsNo.length === 0) {
        loadViewsAction(integrationId, workbookId);
      }
    }
  };

  const _onViewChange = (value: any) => {
    onViewChange(value);

    if (value.value !== viewId) {
      loadViewAction(integrationId, value.value);
    }
  };

  const _onFiltersChange = (event: any) => {
    onFiltersChange(event.target.value);
  };

  const _onVizWidthChange = (event: any) => {
    onVizWidthChange(event.target.value);
  };

  const _onVizHeightChange = (event: any) => {
    onVizHeightChange(event.target.value);
  }

  const _onOptionChange = (callbackFn: any) => (value: any) => {
    callbackFn(value.value);
  }

  const shouldLoadWorkbooks = () => {
    if (workbookOptions.length === 0) {
      loadWorkbooksAction(integrationId);
    }
  };

  const shouldLoadViews = () => {
    if (viewOptions.length === 0 && workbookId) {
      loadViewsAction(integrationId, workbookId);
    }
  };

  const onFilterWorkbook = (keyword: string) => {
    let shouldFilter = keyword.length > 2 && workbooksNo.current >= WORKBOOKS_LIMIT;

    if (keyword.length === 0 && workbookFiltered) {
      setWorkbookFiltered(false);
      shouldFilter = true; // filter for empty keyword so we return all the results
    } else if (shouldFilter) {
      setWorkbookFiltered(true);
    }

    if (!shouldFilter) {
      return;
    }
    /*
     * Make an API call only if keyword is longer than 2 characters, and we have more than WORKBOOKS_LIMIT workbooks
     */
    setLoadWorkbookFilter(true);
    dispatch(loadWorkbooks(uid, integrationId, keyword));
  };

  const onFilterView = (keyword: string) => {
    return false;
  }

  const onFilterCustomView = (keyword: string) => {
    return false;
  }

  const _redirectToExternal = () => {
    if (dashboardUrl) {
      redirectToExternal(dashboardUrl, true);
    }
  };

  return (
    <div className="tableau-form">
      <div className="tableau-form__content">
        <SelectAction
          selected={action}
          onChange={_onActionChange}
          disabled={disabled}
          show={showActions}
        />
        <SelectTableauItem
          label="Workbook"
          selected={workbookId}
          disabled={disabled}
          onChange={_onWorkbookChange}
          options={
            workbookOptions.length > 0 ? workbookOptions : createDefaultOption(workbookName, workbookId)
          }
          onFilter={onFilterWorkbook}
          loading={loadingWorkbooks}
          showLoadingIcon={loadWorkbookFilter}
          onClick={shouldLoadWorkbooks}
        />
        {
          showView && (
            <>
              <SelectTableauItem
                label="View"
                selected={viewId}
                disabled={disabled}
                onChange={_onViewChange}
                options={
                  viewOptions.length > 0 ? viewOptions : createDefaultOption(viewName, viewId)
                }
                onFilter={onFilterView}
                loading={loadingViews}
                onClick={shouldLoadViews}
              />
              {
                showCustomView && showCustomViews && (
                  <SelectTableauItem
                    label="Custom View"
                    selected={customViewId}
                    disabled={customViewOptions.length === 0 || disabled}
                    onChange={onSelectChange(onCustomViewChange)}
                    options={customViewOptions}
                    onFilter={onFilterCustomView}
                    loading={false}
                    unselectDefault={true}
                  />
                )
              }
              {
                showFilters && (
                  <FormGroup inline={true} label="Filters">
                    <InputGroup
                      defaultValue={filters || ''}
                      className="tableau-form__input"
                      onChange={_onFiltersChange}
                      autoComplete="off"
                      placeholder="Filter1=Value1&..."
                      disabled={disabled}
                    />
                  </FormGroup>
                )
              }
              {
                showOptionsFor(action, ACTION_TYPES.VIEW_PDF) && (
                  <>
                    <SelectOptions
                      selected={orientation}
                      onChange={_onOptionChange(onOrientationChange)}
                      label="Orientation"
                      options={PDF_ORIENTATION_OPTIONS}
                      disabled={disabled}
                    />
                    <SelectOptions
                      selected={pageType}
                      onChange={_onOptionChange(onPageTypeChange)}
                      label="Page Type"
                      options={PDF_PAGE_TYPE_OPTIONS}
                      disabled={disabled}
                    />
                    <FormGroup inline={true} label="Viz Height">
                      <InputGroup
                        defaultValue={vizHeight || ''}
                        className="tableau-form__input"
                        onChange={_onVizHeightChange}
                        autoComplete="off"
                        placeholder="Viz Height"
                        disabled={disabled}
                        type={'number'}
                        min={1}
                      />
                    </FormGroup>
                    <FormGroup inline={true} label="Viz Width">
                      <InputGroup
                        defaultValue={vizWidth || ''}
                        className="tableau-form__input"
                        onChange={_onVizWidthChange}
                        autoComplete="off"
                        placeholder="Viz Width"
                        disabled={disabled}
                        type={'number'}
                        min={1}
                      />
                    </FormGroup>
                  </>
                )
              }
              {
                showOptionsFor(action, ACTION_TYPES.VIEW_IMAGE) && (
                  <SelectOptions
                    selected={resolution}
                    onChange={_onOptionChange(onResolutionChange)}
                    label="Resolution"
                    options={IMAGE_RESOLUTION_OPTIONS}
                    disabled={disabled}
                  />
                )
              }
            </>
          )
        }
      </div>
      <PreviewImage
        image={selectedView?.preview_image || null}
        loading={loadingView}
      />
      {
        dashboardUrl && (
          <Button
            className="open-in-tableau-button"
            icon="share"
            text="Open in Tableau"
            onClick={_redirectToExternal}
            disabled={disabled}
            small={true}
          />
        )
      }
    </div>
  );
};

export default TableauForm;
